import { getEnvConfig } from '@hbs-us/contracts';

import { getAppEnv } from '../../lib/env.js';
import type { FleetSearch } from './searchParams.js';

/**
 * Build the credentialed URL for the fleet document-packet download (D-06).
 *
 * The packet engine (the server-rendered `FleetPacketPage`) is REUSED VERBATIM via an api-host
 * GET — it is NOT rebuilt client-side (threat T-22-export). The api host is NOT hardcoded: it
 * comes from `@hbs-us/contracts getEnvConfig().domains.api` (the same source the invoices PDF
 * `exportLinks` uses). The link is opened via `<a>` / new tab with the user's cookies (a safe GET
 * read, no CSRF). The server scopes the packet to the principal tenant (a cross-tenant vehicle id
 * → 404), so the link can never leak another tenant's documents.
 */

function apiBaseUrl(): string {
  return `https://${getEnvConfig(getAppEnv()).domains.api}`;
}

/** Credentialed fleet document-packet download for a vehicle (api-host GET, reused engine). */
export function fleetPacketUrl(vehicleId: number): string {
  return `${apiBaseUrl()}/api/v1/fleet/${vehicleId}/packet`;
}

/**
 * Credentialed "View" link for an entry receipt. The GET route (`/fleet/entries/:id/receipt`) is
 * owned by W2 / plan 22-21 (the file-serving spine) — this plan only LINKS to it. Opened via the
 * `downloadCredentialed` / `openCredentialed` blob path so the in-memory bearer token is attached.
 */
export function fleetEntryReceiptUrl(entryId: number): string {
  return `${apiBaseUrl()}/api/v1/fleet/entries/${entryId}/receipt`;
}

/** Credentialed "View" link for a vehicle document file (W2 / plan 22-21 GET route). */
export function fleetDocumentFileUrl(documentId: number): string {
  return `${apiBaseUrl()}/api/v1/fleet/documents/${documentId}/file`;
}

/**
 * Credentialed CSV export of the filtered fleet entries (F20). Carries the active list filters so
 * the download matches what the operator sees. Opened via `downloadCredentialed` so the in-memory
 * bearer token is attached; the server scopes to the principal tenant (SEC-11).
 */
export function fleetCsvExportUrl(search: FleetSearch): string {
  const params = new URLSearchParams();
  if (search.entryType) params.set('entryType', search.entryType);
  if (search.maintenanceCategory) params.set('maintenanceCategory', search.maintenanceCategory);
  if (search.archived) params.set('archived', '1');
  if (search.search) params.set('search', search.search);
  if (search.dateFrom) params.set('dateFrom', search.dateFrom);
  if (search.dateTo) params.set('dateTo', search.dateTo);
  const qs = params.toString();
  return `${apiBaseUrl()}/api/v1/fleet/export.csv${qs ? `?${qs}` : ''}`;
}
